'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import type { Tenant, TenantProperty, BrandConfig, CurrencyCode } from '@/core/types/tenant.types';
import type { EnvironmentMode } from '@/server/types/context';
import { formatCurrencyAmount, getCurrencySymbol } from '@/core/utils/currency';
import { logger } from '@/core/lib/logger';
import { useAuthContext } from '@/core/providers/AuthProvider';

/* ── Constants ─────────────────────────────────────────────────────── */

/** Known platform domains that do not require tenant resolution */
const PLATFORM_DOMAINS = [
  'hotelstack.io',
  'www.hotelstack.io',
  'localhost',
  '127.0.0.1',
] as const;

/** Default tenant ID used in demo mode */
const DEMO_TENANT_ID = 'demo-tenant-001';
const DEMO_PROPERTY_ID = 'demo-property-001';

/** Storage keys */
const STORAGE_KEY_TENANT = 'tenant_id';
const STORAGE_KEY_PROPERTY = 'property_id';

/* ── Types ─────────────────────────────────────────────────────────── */

interface TenantContextType {
  tenant: Tenant | null;
  currentProperty: TenantProperty | null;
  properties: TenantProperty[];
  switchProperty: (propertyId: string) => void;
  isLoading: boolean;
  error: string | null;
  /* Branding */
  brandConfig: BrandConfig | null;
  applyBranding: (config: BrandConfig) => void;
  /* Currency */
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  formatPrice: (amount: number | null | undefined) => string;
  currencySymbol: string;
  /* Domain */
  isCustomDomain: boolean;
  tenantId: string | null;
  /* Environment mode — this tenant's own DEMO/PRODUCTION setting, from the
     server. `PRODUCTION` until the fetch resolves, so nothing renders demo
     content on the strength of a guess. */
  environmentMode: EnvironmentMode;
  isDemo: boolean;
}

interface DomainResolutionResult {
  tenantId: string;
  propertyId?: string;
  schemaName?: string;
}

/* ── Helpers ───────────────────────────────────────────────────────── */

function isPlatformDomain(hostname: string): boolean {
  return PLATFORM_DOMAINS.some(
    (pd) => hostname === pd || hostname.endsWith(`.${pd}`),
  );
}

/**
 * Ask the server which mode this tenant is in.
 *
 * There was a local `isDemoMode()` here reading `NEXT_PUBLIC_ENVIRONMENT_MODE`,
 * which is inlined at build time — so it answered the same for every tenant a
 * deployment served, and could disagree with the server about the very same
 * request. The mode is a per-tenant database setting; only the server knows it.
 *
 * Returns `'PRODUCTION'` whenever the answer is unavailable. Guessing DEMO (as
 * the old `!== 'PRODUCTION'` default did) shows fabricated data to someone
 * whose real data simply failed to load.
 */
async function fetchEnvironmentMode(): Promise<EnvironmentMode> {
  if (typeof window === 'undefined') return 'PRODUCTION';

  try {
    // Authenticated by the HttpOnly cookie the browser sends automatically.
    // This was gated on a localStorage token that no longer exists after the
    // cookie migration, so it returned early for every real session.
    const res = await fetch('/api/v1/tenant/context', {
      credentials: 'include',
    });
    if (!res.ok) return 'PRODUCTION';
    const body = await res.json();
    return body?.data?.environmentMode === 'DEMO' ? 'DEMO' : 'PRODUCTION';
  } catch {
    return 'PRODUCTION';
  }
}

/**
 * Decode a JWT payload without verification (client-side only).
 * Used to extract tenantId from the access token for context resolution.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Resolve tenant from the stored JWT access token.
 */
/**
 * The pre-migration access token, if this browser still holds one.
 *
 * Kept ONLY so sessions created before the cookie migration keep resolving
 * until they age out. New sessions have nothing here by design, which is why
 * nothing may treat its absence as "signed out".
 */
function readLegacyAccessToken(): string | null {
  try {
    return localStorage.getItem('access_token');
  } catch {
    return null;
  }
}

/**
 * Resolve the tenant from the SERVER, authenticated by the HttpOnly cookie.
 *
 * Under HttpOnly the client cannot read its own JWT, so the token-decoding
 * path below resolves nothing for a current session. Without this the provider
 * fell through to localStorage and then to null, and every page rendered the
 * "No property selected" gate while the session was perfectly valid.
 */
async function resolveTenantFromServer(): Promise<DomainResolutionResult | null> {
  if (typeof window === 'undefined') return null;
  try {
    const res = await fetch('/api/v1/tenant/context', { credentials: 'include' });
    if (!res.ok) return null;
    const body = await res.json();
    const tenantId = body?.data?.tenantId;
    if (typeof tenantId !== 'string' || !tenantId) return null;
    const propertyId = body?.data?.propertyId;
    return {
      tenantId,
      propertyId: typeof propertyId === 'string' ? propertyId : undefined,
    };
  } catch {
    return null;
  }
}

function resolveTenantFromToken(): DomainResolutionResult | null {
  if (typeof window === 'undefined') return null;
  const token = readLegacyAccessToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.tenantId !== 'string') return null;

  return {
    tenantId: payload.tenantId,
    propertyId: typeof payload.propertyId === 'string'
      ? payload.propertyId
      : undefined,
  };
}

/**
 * Resolve tenant from localStorage fallback.
 */
function resolveTenantFromStorage(): DomainResolutionResult | null {
  if (typeof window === 'undefined') return null;
  const tenantId = localStorage.getItem(STORAGE_KEY_TENANT);
  if (!tenantId) return null;

  return {
    tenantId,
    propertyId: localStorage.getItem(STORAGE_KEY_PROPERTY) ?? undefined,
  };
}

/* ── Context ───────────────────────────────────────────────────────── */

const TenantContext = createContext<TenantContextType | null>(null);

/* ── Provider ──────────────────────────────────────────────────────── */

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [currentProperty, setCurrentProperty] = useState<TenantProperty | null>(null);
  const [properties, setProperties] = useState<TenantProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null);
  const [currency, setCurrency] = useState<CurrencyCode>('INR');
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  // PRODUCTION until the server says otherwise — see `fetchEnvironmentMode`.
  const [environmentMode, setEnvironmentMode] = useState<EnvironmentMode>('PRODUCTION');
  /** Increments per resolution; a resolution tagged with an older value is stale. */
  const resolveGenerationRef = useRef(0);

  // The tenant belongs to the SESSION, so it is resolved per signed-in user —
  // not once per page load. Resolving on mount alone kept the previous user's
  // property across a client-side sign-in: a platform admin who signed in where
  // a hotel session had been inherited that hotel, and every hotel-scoped hook
  // gated on `currentProperty` fired and was refused 403 WRONG_PORTAL_TYPE.
  const { user, isLoading: authLoading } = useAuthContext();
  const userId = user?.id ?? null;

  /* ── Property switching ─────────────────────────────────────────── */

  const switchProperty = useCallback((propertyId: string) => {
    const property = properties.find((p) => p.id === propertyId);
    if (property) {
      setCurrentProperty(property);
      if (property.currency) setCurrency(property.currency);
      if (property.brandConfig) setBrandConfig(property.brandConfig);
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_PROPERTY, propertyId);
      }
    }
  }, [properties]);

  /* ── Branding ───────────────────────────────────────────────────── */

  const applyBranding = useCallback((config: BrandConfig) => {
    setBrandConfig(config);
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      if (config.primaryColor) root.style.setProperty('--tenant-primary', config.primaryColor);
      if (config.secondaryColor) root.style.setProperty('--tenant-secondary', config.secondaryColor);
    }
  }, []);

  /* ── Price formatting ───────────────────────────────────────────── */

  /**
   * A price, or an honest gap.
   *
   * `Intl.NumberFormat` renders `null` as 0 and `undefined` as NaN, so a
   * property with no rates displayed as "Rs 0" — read as free, not as "not yet
   * priced". 56 of 137 live properties are in that state: every hotel that has
   * not created a room type (D-26/D-27).
   *
   * "Price on request" is the only rendering a reader cannot mistake for a
   * number.
   */
  const formatPrice = useCallback(
    (amount: number | null | undefined) =>
      typeof amount === 'number' && Number.isFinite(amount) && amount > 0
        ? formatCurrencyAmount(amount, currency)
        : 'Price on request',
    [currency],
  );

  const currencySymbol = getCurrencySymbol(currency);

  /* ── Tenant resolution on mount ─────────────────────────────────── */

  /** `sessionUserId`: the signed-in user this resolution is for; null when signed out. */
  async function resolveTenantContext(hostname: string, customDomain: boolean, sessionUserId: string | null) {
    const generation = ++resolveGenerationRef.current;
    const isStale = () => generation !== resolveGenerationRef.current;

    setIsLoading(true);
    setError(null);
    // Nothing from a previous session may survive into this one.
    setTenant(null);
    setTenantId(null);
    setProperties([]);
    setCurrentProperty(null);

    try {
      let resolved: DomainResolutionResult | null = null;

      // 1. For custom domains, call the domain resolution API
      if (customDomain) {
        resolved = await resolveFromDomainApi(hostname);
        if (isStale()) return;
      }

      // 2. If not resolved via domain, try the JWT (legacy sessions only)
      if (!resolved) {
        resolved = resolveTenantFromToken();
      }

      // 3. Ask the server, authenticated by the HttpOnly cookie.
      //
      // This is the path that serves every CURRENT session: the client cannot
      // read its own token, so step 2 resolves nothing for them. It runs
      // before the storage fallback so a stale stored tenant can never
      // outrank what the server says this cookie is for.
      //
      // Only with a session: signed out (every sign-in, register and reset
      // page) the answer is always 401, and asking anyway logged an API error
      // on each of those pages for a question with a known answer.
      if (!resolved && sessionUserId) {
        resolved = await resolveTenantFromServer();
        // A newer sign-in started while this was in flight; its answer wins.
        if (isStale()) return;
      }

      // 4. Fall back to localStorage — for a session only. A tenant id left
      //    behind by a previous sign-in is not a tenant for a signed-out page.
      if (!resolved && sessionUserId) {
        resolved = resolveTenantFromStorage();
      }

      // There is no demo-tenant fallback. It used to substitute a hardcoded
      // `demo-tenant-001` whenever the build-time flag said DEMO, which meant a
      // failed resolution silently became "you are someone else" — the client
      // twin of the server-side fallback removed from `resolveTenant`.

      if (resolved) {
        setTenantId(resolved.tenantId);
        persistTenantToStorage(resolved);

        // The tenant's own mode, from the server. Awaited here so that
        // `environmentMode` is correct before any consumer renders against it.
        const mode = await fetchEnvironmentMode();
        if (isStale()) return;
        setEnvironmentMode(mode);

        // Fetch real property settings from API if available
        let propCurrency: CurrencyCode = 'INR';
        let propName = 'Hotel';
        let propSlug = '';
        let propCity = '';
        let propCountry = '';
        let propTimezone = 'Asia/Kolkata';

        try {
          // Only a session with a PROPERTY can have hotel settings.
          //
          // /api/v1/hotel/settings goes through withStaffAuth, which answers
          // 403 WRONG_PORTAL_TYPE for a platform or chain user. api-client
          // classifies an authenticated 403 as a fault and writes it to
          // error.log, so a super admin loading any page logged an error and
          // showed `GET /api/v1/hotel/settings 403 (Forbidden)` in the console.
          //
          // Skipping it costs nothing: the values it fills in (property name,
          // currency, timezone) describe a hotel, and there is no hotel here —
          // the defaults below are already the right answer.
          if (resolved.propertyId) {
            // Cookie-authenticated. Gating this on a localStorage token meant
            // the hotel's real name and currency never loaded for a cookie
            // session, and every page rendered the "Hotel"/INR defaults.
            const settingsRes = await fetch('/api/v1/hotel/settings', {
              credentials: 'include',
            });
            if (settingsRes.ok) {
              const settingsData = await settingsRes.json();
              const sp = settingsData?.data?.property;
              if (sp) {
                propCurrency = (sp.currency as CurrencyCode) || 'INR';
                propName = sp.name || propName;
                propCity = sp.city || '';
                propCountry = sp.country || '';
                propTimezone = sp.timezone || propTimezone;
              }
            }
          }
        } catch {
          // Settings fetch failed — use defaults
        }
        if (isStale()) return;

        const propId = resolved.propertyId;

        if (!propId) {
          logger.info('[TenantProvider] No propertyId in scope (expected for platform admin)');
          setIsLoading(false);
          return;
        }
        const resolvedProperty: TenantProperty = {
          id: propId,
          tenantId: resolved.tenantId,
          name: propName,
          slug: propSlug,
          schemaName: '',
          city: propCity,
          country: propCountry,
          timezone: propTimezone,
          currency: propCurrency,
          status: 'ACTIVE',
          isPrimary: true,
          createdAt: new Date().toISOString(),
        };

        setCurrency(propCurrency);

        setTenant({
          id: resolved.tenantId,
          name: propName,
          slug: propSlug,
          type: 'SINGLE',
          status: 'ACTIVE',
          plan: 'ENTERPRISE',
          billingEmail: '',
          properties: [resolvedProperty],
          createdAt: new Date().toISOString(),
        });
        setProperties([resolvedProperty]);
        setCurrentProperty(resolvedProperty);

        logger.info('Tenant resolved', {
          component: 'TenantProvider',
          metadata: {
            tenantId: resolved.tenantId,
            source: customDomain ? 'domain' : 'token/storage',
          },
        });
      } else if (customDomain) {
        // A CUSTOM domain that resolves to no tenant is a real fault: the
        // hostname exists in DNS and points here, but maps to nothing we can
        // serve. Someone's hotel site is down.
        logger.warn('No tenant context could be resolved', {
          component: 'TenantProvider',
          metadata: { hostname, customDomain: true },
        });
      } else {
        // A PLATFORM domain with no tenant in scope is the normal state for a
        // platform admin who has not selected a property — not a fault.
        //
        // This used to warn unconditionally, and production showed the cost:
        // 22 WARN entries in the 22 minutes after the v1.0.0 deploy, all of
        // them this line, all of them describing an expected condition. Same
        // species as the three app-wide 403s a super admin was triggering —
        // an expected state reported as a problem.
        logger.info('No tenant in scope (platform domain)', {
          component: 'TenantProvider',
          metadata: { hostname, customDomain: false },
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tenant resolution failed';
      setError(message);
      logger.error('Tenant resolution error', {
        component: 'TenantProvider',
        metadata: { hostname, error: message },
      });

      // No demo-tenant fallback on error either — the failure is surfaced via
      // `error` above, which is what consumers should render.
    } finally {
      // A superseded run must not clear the loading state of the one replacing it.
      if (!isStale()) setIsLoading(false);
    }
  }

  /** The user the current resolution was made for; `undefined` = never resolved. */
  const resolvedForUserRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Wait for auth to settle: the server answers for the cookie, and resolving
    // mid-sign-in would answer for the session that is being replaced.
    if (authLoading) return;
    if (resolvedForUserRef.current === userId) return;
    resolvedForUserRef.current = userId;

    const hostname = window.location.hostname;
    const customDomain = !isPlatformDomain(hostname);
    setIsCustomDomain(customDomain);

    resolveTenantContext(hostname, customDomain, userId);
  }, [userId, authLoading]);

  return (
    <TenantContext.Provider value={{
      tenant, currentProperty, properties, switchProperty, isLoading, error,
      brandConfig, applyBranding,
      currency, setCurrency, formatPrice, currencySymbol,
      isCustomDomain, tenantId, environmentMode, isDemo: environmentMode === 'DEMO',
    }}>
      {children}
    </TenantContext.Provider>
  );
}

/* ── API resolution helper ─────────────────────────────────────────── */

async function resolveFromDomainApi(
  domain: string,
): Promise<DomainResolutionResult | null> {
  try {
    const baseUrl = typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost:6100';

    const response = await fetch(`${baseUrl}/api/v1/public/resolve-domain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    });

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Domain resolution failed: ${response.status}`);
    }

    const json = await response.json();
    return json.data as DomainResolutionResult;
  } catch (err) {
    logger.error('Domain resolution API call failed', {
      component: 'TenantProvider',
      metadata: { domain, error: String(err) },
    });
    return null;
  }
}

/* ── Storage persistence ───────────────────────────────────────────── */

function persistTenantToStorage(result: DomainResolutionResult): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY_TENANT, result.tenantId);
  if (result.propertyId) {
    localStorage.setItem(STORAGE_KEY_PROPERTY, result.propertyId);
  }
}

/* ── Hook ──────────────────────────────────────────────────────────── */

export function useTenantContext() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenantContext must be used within TenantProvider');
  return ctx;
}
